<template>
  <v-card
    id="screenContent"
    class="vetalfa-view"
    :max-width="_getMaxWidth()"
    flat
  >
    <div class="vetalfa-global-header" ref="screenHeader">
      <vetalfa-small-header
        showBreadCrumbAndTitle
        :title="_getLabel('PATIENT_RECORD')"
        :breadcrumb="_getLabel('PATIENT_RECORD')"
      ></vetalfa-small-header>
      <v-row no-gutters justify="center">
        <v-row no-gutters align="center" class="vetalfa-max-width">
          <v-col cols="12" class="px-4 my-2">
            <patients context="records" />
          </v-col>
        </v-row>
      </v-row>
      <v-row
        no-gutters
        justify="center"
        v-if="patient && (vaccinationSchedule.length || activeHospitalization)"
      >
        <v-row no-gutters align="center" class="vetalfa-max-width">
          <v-col cols="12" class="px-4 pb-1">
            <div class="clinical-record-notices d-flex flex-wrap">
              <v-chip
                v-if="activeHospitalization"
                small
                outlined
                color="#B42318"
                class="clinical-record-notice"
              >
                <v-icon small left>{{ mdiBedOutline }}</v-icon>
                {{ _getLabel('PATIENT_ADMITTED') }}
              </v-chip>
              <v-chip
                v-if="vaccinationSchedule.length"
                small
                outlined
                color="#9C5A00"
                class="clinical-record-notice"
              >
                <v-icon small left>{{ mdiNeedle }}</v-icon>
                {{ vaccinationSchedule.length }} {{ _getLabel('PENDING_VACCINES').toLowerCase() }}
              </v-chip>
            </div>
          </v-col>
        </v-row>
      </v-row>
      <v-row no-gutters justify="center" v-if="patient">
        <v-row no-gutters align="center" class="vetalfa-max-width">
          <v-col cols="12" class="px-4 py-2">
            <div class="clinical-record-primary-actions d-flex align-center">
              <v-btn
                rounded
                depressed
                color="primary"
                aria-label="Iniciar nova consulta"
                class="text-none white--text clinical-record-action clinical-record-main-action px-4"
                @click="createEncounter"
              >
                <v-icon small left>{{ mdiStethoscope }}</v-icon>
                Consulta
              </v-btn>
              <v-btn
                rounded
                outlined
                color="primary"
                aria-label="Criar prescrição"
                class="text-none clinical-record-action clinical-record-prescription-action px-4"
                @click="newPrescription"
              >
                <v-icon small left>{{ mdiFileDocumentEditOutline }}</v-icon>
                Prescrição
              </v-btn>
              <template v-if="!_xsOnly()">
                <v-btn
                  rounded
                  depressed
                  color="secondary"
                  class="text-none primary--text clinical-record-action clinical-record-category-action"
                  @click="openNewVaccination"
                >
                  <v-icon small left color="#9C5A00">{{ mdiNeedle }}</v-icon>
                  {{ _getLabel('VACCINATION') }}
                </v-btn>
                <v-btn
                  rounded
                  depressed
                  color="secondary"
                  class="text-none primary--text clinical-record-action clinical-record-category-action"
                  @click="openNewLabExam"
                >
                  <v-icon small left color="#7A2993">{{ mdiTestTube }}</v-icon>
                  {{ _getLabel('LAB_EXAM') }}
                </v-btn>
                <v-btn
                  rounded
                  depressed
                  color="secondary"
                  class="text-none primary--text clinical-record-action clinical-record-category-action"
                  @click="openNewHospitalization"
                >
                  <v-icon small left color="#B42318">{{ mdiBedOutline }}</v-icon>
                  {{ _getLabel('HOSPITALIZATION') }}
                </v-btn>
              </template>
              <v-menu v-else offset-y left>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    rounded
                    outlined
                    color="primary"
                    aria-label="Adicionar registro"
                    class="text-none clinical-record-action clinical-record-menu-trigger"
                    v-bind="attrs"
                    v-on="on"
                  >
                    <v-icon small left>{{ mdiPlus }}</v-icon>
                    Registro
                    <v-icon small right>{{ mdiChevronDown }}</v-icon>
                  </v-btn>
                </template>
                <v-list dense class="clinical-record-menu py-2">
                  <v-subheader class="caption text-uppercase font-weight-bold">
                    Adicionar ao prontuário
                  </v-subheader>
                  <v-list-item @click="openNewVaccination">
                    <v-list-item-icon><v-icon color="primary">{{ mdiNeedle }}</v-icon></v-list-item-icon>
                    <v-list-item-content><v-list-item-title>{{ _getLabel('VACCINATION') }}</v-list-item-title></v-list-item-content>
                  </v-list-item>
                  <v-list-item @click="openNewLabExam">
                    <v-list-item-icon><v-icon color="primary">{{ mdiTestTube }}</v-icon></v-list-item-icon>
                    <v-list-item-content><v-list-item-title>{{ _getLabel('LAB_EXAM') }}</v-list-item-title></v-list-item-content>
                  </v-list-item>
                  <v-list-item @click="openNewHospitalization">
                    <v-list-item-icon><v-icon color="primary">{{ mdiBedOutline }}</v-icon></v-list-item-icon>
                    <v-list-item-content><v-list-item-title>{{ _getLabel('HOSPITALIZATION') }}</v-list-item-title></v-list-item-content>
                  </v-list-item>
                </v-list>
              </v-menu>
            </div>
          </v-col>
        </v-row>
      </v-row>
      <v-row no-gutters justify="center" v-if="patient">
        <v-row no-gutters align="center" class="vetalfa-max-width">
          <v-col cols="12">
            <v-divider></v-divider>
          </v-col>
        </v-row>
      </v-row>
    </div>

    <div ref="screenContent">
      <v-row
        v-if="!patient"
        no-gutters
        class="ma-0 pa-0"
        justify="center"
      >
        <v-col cols="12" class="px-4 my-2">
          <v-alert
            @click="routeRedirect({ name: 'accounts' })"
            :icon="mdiAlertCircleOutline"
            outlined
            color="grey darken-1"
            class="vetalfa-pointer vetalfa-card-grey-border"
          >
            <p
              class="subtitle-2 grey--text text--darken-1 ma-0 pa-0 mb-1"
              v-html="_getLabel('CLICK_TO_EDIT_YOUR_MEDICAL_REGISTRATION')"
            ></p>
          </v-alert>
        </v-col>
        <v-col cols="12" class="px-4 my-4">
          <p class="text-center subtitle-2 grey--text text--darken-1">
            {{ _getLabel('SELECT_A_PATIENT_FOR_RECORDS') }}
          </p>
        </v-col>
      </v-row>

      <patient-timeline
        v-else
        :items="filteredTimeline"
        :loading="timelineLoading"
        :loadingMore="appendingEncounters"
        :filter="timelineFilter"
        :hideDrafts="hideDrafts"
        :truncated="truncated"
        :hasMoreEncounters="hasMoreTimeline"
        :hasLoadErrors="Object.keys(timelinePaginationErrors).length > 0"
        @retry="retryTimeline"
        @openEncounter="openEncounter"
        @openVaccination="openVaccinationEdit"
        @openLabExam="openLabExamEdit"
        @openHospitalization="openHospitalizationEdit"
        @openPrescription="openPrescription"
        @finalizeVaccination="confirmFinalizeVaccination"
        @removeVaccination="confirmRemoveVaccination"
        @finalizeLabExam="confirmFinalizeLabExam"
        @removeLabExam="confirmRemoveLabExam"
        @attachLabExamFile="handleAttachLabExamFile"
        @openLabExamFile="openLabExamFile"
        @addEvolution="openEvolutionDialog"
        @dischargeHospitalization="openDischargeDialog"
        @removeHospitalization="confirmRemoveHospitalization"
        @loadMoreEncounters="loadMoreRecords"
        @filterChange="setTimelineFilter"
        @toggleHideDrafts="toggleHideDrafts"
      />

    </div>

    <vetalfa-dialog
      :toggleModel="showVaccinationDialog"
      :bottom="_xsOnly()"
      :fullscreen="false"
      :height="_getDialogHeight(0.92)"
      max-width="600px"
      :smallHeader="false"
      :featureMode="false"
      :dialogHeader="{ title: editingVaccination && editingVaccination.id ? _getLabel('VACCINATION') : _getLabel('NEW_VACCINATION') }"
      persistent
      contentClasses="clinical-record-bottom-sheet"
      @dialogClose="requestCloseVaccinationDialog"
    >
      <div class="clinical-record-dialog-content px-4 pt-3">
        <v-alert
          v-if="isEditingVaccinationFinalized"
          dense
          outlined
          color="success"
          class="mb-2"
        >
          <span class="caption">{{ _getLabel('RECORD_FINALIZE_CONFLICT') }}</span>
        </v-alert>
        <vaccination-form
          v-model="editingVaccination"
          :species="patientSpecies"
          :readonly="isEditingVaccinationFinalized"
        />
      </div>
      <template #actions>
        <div class="clinical-record-dialog-actions px-4 pb-4">
          <v-btn
            v-if="!isEditingVaccinationFinalized"
            rounded
            color="primary"
            class="text-none"
            block
            :disabled="!editingVaccination || !editingVaccination.vaccine || isEditingVaccinationFinalized"
            :loading="savingVaccination"
            @click="handleSaveVaccination"
          >
            {{ _getLabel('SAVE') }}
          </v-btn>
        </div>
      </template>
    </vetalfa-dialog>

    <vetalfa-dialog
      :toggleModel="showLabExamDialog"
      :bottom="_xsOnly()"
      :fullscreen="false"
      :height="_getDialogHeight(0.92)"
      max-width="600px"
      :smallHeader="false"
      :featureMode="false"
      :dialogHeader="{ title: editingLabExam && editingLabExam.id ? _getLabel('LAB_EXAM') : _getLabel('NEW_LAB_EXAM') }"
      persistent
      contentClasses="clinical-record-bottom-sheet"
      @dialogClose="requestCloseLabExamDialog"
    >
      <div class="clinical-record-dialog-content px-4 pt-3">
        <v-alert
          v-if="isEditingLabExamFinalized"
          dense
          outlined
          color="success"
          class="mb-2"
        >
          <span class="caption">{{ _getLabel('RECORD_FINALIZE_CONFLICT') }}</span>
        </v-alert>
        <lab-exam-form v-model="editingLabExam" :readonly="isEditingLabExamFinalized" />
      </div>
      <template #actions>
        <div class="clinical-record-dialog-actions px-4 pb-4">
          <v-btn
            v-if="!isEditingLabExamFinalized"
            rounded
            color="primary"
            class="text-none"
            block
            :disabled="!editingLabExam || !editingLabExam.name || isEditingLabExamFinalized"
            :loading="savingLabExam"
            @click="handleSaveLabExam"
          >
            {{ _getLabel('SAVE') }}
          </v-btn>
        </div>
      </template>
    </vetalfa-dialog>

    <vetalfa-dialog
      :toggleModel="showHospitalizationDialog"
      :bottom="_xsOnly()"
      :fullscreen="false"
      :height="_getDialogHeight(0.92)"
      max-width="600px"
      :smallHeader="false"
      :featureMode="false"
      :dialogHeader="{ title: editingHospitalization && editingHospitalization.id ? _getLabel('HOSPITALIZATION') : _getLabel('NEW_HOSPITALIZATION') }"
      persistent
      contentClasses="clinical-record-bottom-sheet"
      @dialogClose="requestCloseHospitalizationDialog"
    >
      <div class="clinical-record-dialog-content px-4 pt-3">
        <v-alert
          v-if="isEditingHospitalizationFinalized"
          dense
          outlined
          color="success"
          class="mb-2"
        >
          <span class="caption">{{ _getLabel('RECORD_FINALIZE_CONFLICT') }}</span>
        </v-alert>
        <hospitalization-form v-model="editingHospitalization" :readonly="isEditingHospitalizationFinalized" />
      </div>
      <template #actions>
        <div class="clinical-record-dialog-actions px-4 pb-4">
          <v-btn
            v-if="!isEditingHospitalizationFinalized"
            rounded
            color="primary"
            class="text-none"
            block
            :disabled="!editingHospitalization || !editingHospitalization.reason || isEditingHospitalizationFinalized"
            :loading="savingHospitalization"
            @click="handleSaveHospitalization"
          >
            {{ _getLabel('SAVE') }}
          </v-btn>
        </div>
      </template>
    </vetalfa-dialog>

    <vetalfa-dialog
      :toggleModel="showEvolutionDialog"
      :bottom="_xsOnly()"
      :fullscreen="false"
      :height="_getDialogHeight(0.92)"
      max-width="600px"
      :smallHeader="false"
      :featureMode="false"
      :dialogHeader="{ title: _getLabel('NEW_EVOLUTION') }"
      persistent
      contentClasses="clinical-record-bottom-sheet"
      @dialogClose="requestCloseEvolutionDialog"
    >
      <div class="clinical-record-dialog-content px-4 pt-3">
        <evolution-form v-model="editingEvolution" />
      </div>
      <template #actions>
        <div class="clinical-record-dialog-actions px-4 pb-4">
          <v-btn
            rounded
            color="primary"
            class="text-none"
            block
            :disabled="!hasEvolutionNotes"
            :loading="savingEvolution"
            @click="handleSaveEvolution"
          >
            {{ _getLabel('SAVE') }}
          </v-btn>
        </div>
      </template>
    </vetalfa-dialog>

    <vetalfa-dialog
      :toggleModel="showDischargeDialog"
      :bottom="_xsOnly()"
      :fullscreen="false"
      :height="_getDialogHeight(0.92)"
      max-width="600px"
      :smallHeader="false"
      :featureMode="false"
      :dialogHeader="{ title: _getLabel('DISCHARGE_TITLE') }"
      persistent
      contentClasses="clinical-record-bottom-sheet"
      @dialogClose="requestCloseDischargeDialog"
    >
      <div class="clinical-record-dialog-content px-4 pt-3">
        <v-row no-gutters>
          <v-col cols="12">
            <v-text-field
              v-model="dischargeDateFormatted"
              outlined
              dense
              :label="_getLabel('DISCHARGE_DATE')"
              type="date"
            ></v-text-field>
          </v-col>
          <v-col cols="12">
            <v-text-field
              v-model="dischargeModel.dischargeCondition"
              outlined
              dense
              :label="_getLabel('DISCHARGE_CONDITION')"
            ></v-text-field>
          </v-col>
          <v-col cols="12">
            <v-textarea
              v-model="dischargeModel.dischargeSummary"
              outlined
              dense
              :label="_getLabel('DISCHARGE_SUMMARY')"
              rows="3"
            ></v-textarea>
          </v-col>
        </v-row>
      </div>
      <template #actions>
        <div class="clinical-record-dialog-actions px-4 pb-4">
          <v-btn
            rounded
            color="primary"
            class="text-none"
            block
            :disabled="!isDischargeComplete"
            :loading="savingDischarge"
            @click="confirmDischarge"
          >
            {{ _getLabel('DISCHARGE') }}
          </v-btn>
        </div>
      </template>
    </vetalfa-dialog>

    <clinical-draft-dialog
      :value="recordDraftDialog"
      :recovery="!!recordRecoveryDraft"
      :busy="recordDraftBusy"
      :error="recordDraftError"
      :entityLabel="recordDraftEntityLabel"
      @restore="restoreRecordDraft"
      @discard="discardRecordDraft"
      @keep="keepRecordDraftAndClose"
      @cancel="cancelRecordDraftDecision"
    />
  </v-card>
</template>

<style scoped>
#screenContent { background:#ffffff; }
.vetalfa-global-header { box-shadow:0 1px 0 rgba(25,49,83,.08); }
.vetalfa-global-header .v-btn { letter-spacing:0; }
.vetalfa-global-header .v-btn--outlined { border-color:#d8dfeb; }
.v-bottom-sheet .v-card { border-radius:16px 16px 0 0; }
@media (min-width: 600px) {
  .v-bottom-sheet .v-card { border-radius:16px; margin:24px auto; max-width:600px; }
}
@media (max-width: 599px) {
  #screenContent { background:#f5f6fa; }
}
</style>

<script>
import clinicalLayout from "@/mixin/clinical-layout.js";
import { mapActions, mapGetters, mapMutations } from "vuex";
import Patients from "@/components/Patients.vue";
import PatientTimeline from "@/components/PatientTimeline.vue";
import VaccinationForm from "@/components/VaccinationForm.vue";
import LabExamForm from "@/components/LabExamForm.vue";
import HospitalizationForm from "@/components/HospitalizationForm.vue";
import EvolutionForm from "@/components/EvolutionForm.vue";
import ClinicalDraftDialog from "@/components/ClinicalDraftDialog.vue";
import clinicalDrafts from "@/services/clinical-drafts.js";
import {
  timestampToDateInput,
  dateInputToClinicalTimestamp,
} from "@/constants/records.utils.js";
import {
  mdiAlertCircleOutline,
  mdiBedOutline,
  mdiChevronDown,
  mdiFileDocumentEditOutline,
  mdiNeedle,
  mdiPlus,
  mdiStethoscope,
  mdiTestTube,
} from "@mdi/js";

// Mesmos limites do backend (`functions/utils/files.utils.js`): a validação
// local existe para avisar o usuário antes de enviar 10 MB pela rede.
const MAX_FILE_SIZE = 10 * 1024 * 1024;
const ALLOWED_FILE_TYPES = ["application/pdf", "image/jpeg", "image/png"];

export default {
  name: "PatientRecord",
  mixins: [clinicalLayout],
  components: {
    patients: Patients,
    "patient-timeline": PatientTimeline,
    "vaccination-form": VaccinationForm,
    "lab-exam-form": LabExamForm,
    "hospitalization-form": HospitalizationForm,
    "evolution-form": EvolutionForm,
    "clinical-draft-dialog": ClinicalDraftDialog,
  },
  props: {
    patientId: {
      type: String,
      default: null,
      required: false,
    },
  },
  data() {
    return {
      loadRequestId: 0,
      mdiAlertCircleOutline,
      mdiNeedle,
      mdiBedOutline,
      mdiChevronDown,
      mdiFileDocumentEditOutline,
      mdiPlus,
      mdiStethoscope,
      mdiTestTube,
      showVaccinationDialog: false,
      editingVaccination: {},
      savingVaccination: false,
      showLabExamDialog: false,
      editingLabExam: {},
      savingLabExam: false,
      showHospitalizationDialog: false,
      editingHospitalization: {},
      savingHospitalization: false,
      showEvolutionDialog: false,
      editingEvolution: { notes: "", vitalSigns: {}, medications: "" },
      evolutionHospitalization: null,
      savingEvolution: false,
      showDischargeDialog: false,
      dischargeModel: {
        dischargeDate: null,
        dischargeCondition: "",
        dischargeSummary: "",
      },
      dischargingHospitalization: null,
      savingDischarge: false,
      appendingEncounters: false,
      recordDraftKind: null,
      recordDraftRecordId: null,
      recordDraftBaseline: null,
      recordDraftDialog: false,
      recordRecoveryDraft: null,
      recordDraftError: "",
      recordDraftTimer: null,
      recordDraftClosing: false,
      pendingRecordNavigation: null,
    };
  },
  computed: {
    ...mapGetters("patients", ["patient"]),
    ...mapGetters("general", ["toggles", "user"]),
    ...mapGetters("records", [
      "encounters",
      "loadingEncounters",
      "filteredTimeline",
      "timelineFilter",
      "hideDrafts",
      "vaccinationSchedule",
      "loadingVaccinations",
      "loadingLabExams",
      "loadingHospitalizations",
      "loadingPrescriptions",
      "hasMoreEncounters",
      "hasMoreTimeline",
      "timelinePaginationErrors",
      "truncated",
      "activeHospitalization",
    ]),
    /**
     * Durante o "Carregar mais consultas" a lista continua na tela: trocar tudo
     * por "Carregando..." esconderia justamente o que o usuario acabou de ler.
     */
    timelineLoading() {
      if (this.appendingEncounters) return false;
      return (
        this.loadingEncounters ||
        this.loadingVaccinations ||
        this.loadingLabExams ||
        this.loadingHospitalizations ||
        this.loadingPrescriptions
      );
    },
    isEditingVaccinationFinalized() {
      return !!(this.editingVaccination && this.editingVaccination.finalizedAt);
    },
    isEditingLabExamFinalized() {
      return !!(this.editingLabExam && this.editingLabExam.finalizedAt);
    },
    isEditingHospitalizationFinalized() {
      return !!(
        this.editingHospitalization && this.editingHospitalization.finalizedAt
      );
    },
    hasEvolutionNotes() {
      return !!(
        this.editingEvolution &&
        typeof this.editingEvolution.notes === "string" &&
        this.editingEvolution.notes.trim()
      );
    },
    dischargeDateFormatted: {
      get() {
        if (!this.dischargeModel.dischargeDate) return "";
        return timestampToDateInput(this.dischargeModel.dischargeDate);
      },
      set(val) {
        this.dischargeModel.dischargeDate = dateInputToClinicalTimestamp(val) || null;
      },
    },
    isDischargeComplete() {
      const model = this.dischargeModel || {};
      return !!(
        model.dischargeDate &&
        (model.dischargeCondition || "").trim() &&
        (model.dischargeSummary || "").trim()
      );
    },
    patientSpecies() {
      return this.patient && this.patient.species ? this.patient.species : 0;
    },
    routePatientId() {
      return this.patientId || this.$route.params.patientId || null;
    },
    hasRecordsToggle() {
      return !!(this.toggles && this.toggles["patient_records"]);
    },
    hasPrescriptionsToggle() {
      return !!(this.toggles && this.toggles["prescriptions"]);
    },
    /**
     * `/prescriptions` e a unica porta de entrada da prescricao (a tela leva a
     * consulta, onde fica o construtor), entao ela continua aberta para quem tem
     * so `prescriptions` ligado: no app mobile e nas organizacoes fora do piloto
     * `patient_records` e sempre falso. A rota direta do prontuario
     * (`/patients/:patientId/record`) exige `patient_records`.
     */
    canAccessRoute() {
      return this.hasRecordsToggle;
    },
    recordDraftModel() {
      const models = {
        vaccination: this.editingVaccination,
        labExam: this.editingLabExam,
        hospitalization: this.editingHospitalization,
        evolution: this.editingEvolution,
        discharge: this.dischargeModel,
      };
      return models[this.recordDraftKind] || null;
    },
    recordDraftEntityLabel() {
      return {
        vaccination: "vacina",
        labExam: "solicitação de exame",
        hospitalization: "internação",
        evolution: "evolução",
        discharge: "alta",
      }[this.recordDraftKind] || "informação clínica";
    },
    recordDraftContext() {
      if (!this.recordDraftKind || !this.recordDraftRecordId) return null;
      return {
        userId: this.user && this.user.uid,
        organizationId: (this.user && this.user.organizationId) || null,
        patientId: this.patient && this.patient.id,
        encounterId: `record-${this.recordDraftKind}-${this.recordDraftRecordId}`,
      };
    },
    recordDraftDirty() {
      if (!this.recordDraftKind || this.recordDraftBaseline === null) return false;
      return JSON.stringify(this.recordDraftModel || {}) !== this.recordDraftBaseline;
    },
    recordDraftBusy() {
      return !!({
        vaccination: this.savingVaccination,
        labExam: this.savingLabExam,
        hospitalization: this.savingHospitalization,
        evolution: this.savingEvolution,
        discharge: this.savingDischarge,
      }[this.recordDraftKind]);
    },
  },
  methods: {
    async retryTimeline() {
      await Promise.all(Object.keys(this.timelinePaginationErrors).map(type =>
        this.$store.dispatch('records/fetchTimelineType', { type, append: !!this.$store.state.records.pagination[type].cursor })
      ));
    },
    async newPrescription() {
      const result = await this.$store.dispatch('prescriptions/initializeEncounterPrescriptionAction', {
        patientId: this.patient && this.patient.id,
        encounterId: null,
      });
      if (result && result.ok) this.$router.push({ path: '/prescriptions', query: { patientId: this.patient && this.patient.id } });
    },
    ...mapActions("records", [
      "fetchEncounters",
      "fetchVaccinations",
      "fetchVaccinationSchedule",
      "saveVaccination",
      "deleteVaccination",
      "fetchLabExams",
      "saveLabExam",
      "deleteLabExam",
      "uploadLabExamFile",
      "fetchHospitalizations",
      "saveHospitalization",
      "deleteHospitalization",
      "addEvolution",
      "fetchPrescriptions",
      "loadMoreTimeline",
      "setTimelineFilter",
      "toggleHideDrafts",
      "createEncounter",
      "clearRecords",
    ]),
    ...mapActions("prescriptions", [
      "checkForMedicalData",
      "openPrescriptionFromPatientAction",
    ]),
    ...mapActions("patients", ["selectPatientById"]),
    ...mapActions("general", [
      "routeRedirect",
      "routePathRedirect",
      "showAlert",
      "showMessage",
    ]),
    ...mapMutations("general", ["setConfirmModel"]),
    openEncounter(encounter) {
      this.$router.push({
        path: `/patients/${this.patient.id}/encounters/${encounter.id}`,
      });
    },
    cloneRecordDraft(value) {
      return JSON.parse(JSON.stringify(value || {}));
    },
    setRecordDraftModel(kind, value) {
      const cloned = this.cloneRecordDraft(value);
      if (kind === "vaccination") this.editingVaccination = cloned;
      if (kind === "labExam") this.editingLabExam = cloned;
      if (kind === "hospitalization") this.editingHospitalization = cloned;
      if (kind === "evolution") this.editingEvolution = cloned;
      if (kind === "discharge") this.dischargeModel = cloned;
    },
    setRecordDialogVisible(kind, visible) {
      if (kind === "vaccination") this.showVaccinationDialog = visible;
      if (kind === "labExam") this.showLabExamDialog = visible;
      if (kind === "hospitalization") this.showHospitalizationDialog = visible;
      if (kind === "evolution") this.showEvolutionDialog = visible;
      if (kind === "discharge") this.showDischargeDialog = visible;
    },
    async beginRecordEditing(kind, model, recordId = null) {
      this.cancelRecordDraftTimer();
      this.recordDraftKind = null;
      this.setRecordDraftModel(kind, model);
      this.recordDraftBaseline = JSON.stringify(this.cloneRecordDraft(model));
      this.recordDraftKind = kind;
      this.recordDraftRecordId = String(recordId || (model && model.id) || "new");
      this.recordRecoveryDraft = null;
      this.recordDraftDialog = false;
      this.recordDraftError = "";
      this.setRecordDialogVisible(kind, true);
      try {
        const draft = await clinicalDrafts.load(this.recordDraftContext);
        if (this.recordDraftKind !== kind) return;
        if (draft && draft.invalid) {
          this.showAlert({
            text: "A cópia local deste registro estava indisponível e foi descartada.",
            color: "warning",
          });
        } else if (draft) {
          this.recordRecoveryDraft = draft;
          this.recordDraftDialog = true;
        }
      } catch {
        this.recordDraftError = "Não foi possível verificar a cópia local.";
      }
    },
    cancelRecordDraftTimer() {
      if (this.recordDraftTimer) clearTimeout(this.recordDraftTimer);
      this.recordDraftTimer = null;
    },
    scheduleRecordDraftPersistence() {
      if (!this.recordDraftDirty || this.recordRecoveryDraft || this.recordDraftBusy) return;
      this.cancelRecordDraftTimer();
      this.recordDraftTimer = setTimeout(() => {
        this.recordDraftTimer = null;
        this.persistRecordDraft();
      }, 600);
    },
    async persistRecordDraft() {
      this.cancelRecordDraftTimer();
      const context = this.recordDraftContext && { ...this.recordDraftContext };
      if (!context || !this.recordDraftDirty || this.recordRecoveryDraft) return true;
      const kind = this.recordDraftKind;
      const record = this.cloneRecordDraft(this.recordDraftModel);
      try {
        await clinicalDrafts.save(context, { record, kind });
        if (kind === this.recordDraftKind) this.recordDraftError = "";
        return true;
      } catch {
        if (kind === this.recordDraftKind) {
          this.recordDraftError = "Não foi possível guardar a cópia local. Salve antes de sair.";
        }
        return false;
      }
    },
    async removeCurrentRecordDraft() {
      const context = this.recordDraftContext && { ...this.recordDraftContext };
      if (!context) return;
      try {
        await clinicalDrafts.remove(context);
      } catch {
        // O registro salvo na conta continua válido; a cópia expira em sete dias.
      }
    },
    requestCloseRecordDialog(kind) {
      if (this.recordDraftClosing || kind !== this.recordDraftKind) return;
      this.setRecordDialogVisible(kind, false);
      if (!this.recordDraftDirty) return this.closeRecordEditor();
      this.recordRecoveryDraft = null;
      this.recordDraftError = "";
      this.recordDraftDialog = true;
    },
    requestCloseVaccinationDialog() {
      this.requestCloseRecordDialog("vaccination");
    },
    requestCloseLabExamDialog() {
      this.requestCloseRecordDialog("labExam");
    },
    requestCloseHospitalizationDialog() {
      this.requestCloseRecordDialog("hospitalization");
    },
    requestCloseEvolutionDialog() {
      this.requestCloseRecordDialog("evolution");
    },
    requestCloseDischargeDialog() {
      this.requestCloseRecordDialog("discharge");
    },
    async restoreRecordDraft() {
      const saved = this.recordRecoveryDraft && this.recordRecoveryDraft.data;
      if (!saved || !saved.record) return;
      this.setRecordDraftModel(this.recordDraftKind, saved.record);
      this.recordRecoveryDraft = null;
      this.recordDraftDialog = false;
      this.recordDraftError = "";
    },
    async discardRecordDraft() {
      try {
        await this.removeCurrentRecordDraft();
        const wasRecovery = !!this.recordRecoveryDraft;
        this.recordRecoveryDraft = null;
        this.recordDraftDialog = false;
        this.recordDraftError = "";
        if (!wasRecovery) {
          const navigation = this.pendingRecordNavigation;
          this.pendingRecordNavigation = null;
          this.closeRecordEditor();
          if (navigation) navigation();
        }
      } catch {
        this.recordDraftError = "Não foi possível descartar a cópia local.";
      }
    },
    async keepRecordDraftAndClose() {
      if (!(await this.persistRecordDraft())) return;
      const navigation = this.pendingRecordNavigation;
      this.pendingRecordNavigation = null;
      this.recordDraftDialog = false;
      this.closeRecordEditor();
      if (navigation) navigation();
    },
    cancelRecordDraftDecision() {
      this.recordDraftDialog = false;
      if (this.pendingRecordNavigation) {
        this.pendingRecordNavigation(false);
        this.pendingRecordNavigation = null;
      }
      if (!this.recordRecoveryDraft && this.recordDraftKind) {
        const kind = this.recordDraftKind;
        this.$nextTick(() => this.setRecordDialogVisible(kind, true));
      }
    },
    closeRecordEditor() {
      const kind = this.recordDraftKind;
      this.recordDraftClosing = true;
      this.cancelRecordDraftTimer();
      if (kind) this.setRecordDialogVisible(kind, false);
      this.recordDraftKind = null;
      this.recordDraftRecordId = null;
      this.recordDraftBaseline = null;
      this.recordRecoveryDraft = null;
      this.recordDraftDialog = false;
      this.recordDraftError = "";
      this.editingVaccination = {};
      this.editingLabExam = {};
      this.editingHospitalization = {};
      this.editingEvolution = { notes: "", vitalSigns: {}, medications: "" };
      this.evolutionHospitalization = null;
      this.dischargeModel = {
        dischargeDate: null,
        dischargeCondition: "",
        dischargeSummary: "",
      };
      this.dischargingHospitalization = null;
      this.$nextTick(() => {
        this.recordDraftClosing = false;
      });
    },
    async finishSavedRecord() {
      await this.removeCurrentRecordDraft();
      this.closeRecordEditor();
    },
    openNewVaccination() {
      this.beginRecordEditing("vaccination", { dose: 1 });
    },
    openVaccinationEdit(vaccination) {
      this.beginRecordEditing("vaccination", vaccination, vaccination.id);
    },
    closeVaccinationDialog() {
      this.closeRecordEditor();
    },
    async handleSaveVaccination() {
      this.savingVaccination = true;
      try {
        const result = await this.saveVaccination({
          vaccination: this.editingVaccination,
          showMessage: false,
          showError: false,
        });
        if (!result || !result.ok) {
          return this.showRecordError(result && result.error);
        }
        this.showMessage();
        await this.finishSavedRecord();
        if (this.patient) {
          await this.fetchVaccinationSchedule(this.patient.id);
        }
      } finally {
        this.savingVaccination = false;
      }
    },
    openNewLabExam() {
      this.beginRecordEditing("labExam", { type: 0, status: 0 });
    },
    openLabExamEdit(labExam) {
      this.beginRecordEditing("labExam", labExam, labExam.id);
    },
    closeLabExamDialog() {
      this.closeRecordEditor();
    },
    async handleSaveLabExam() {
      this.savingLabExam = true;
      try {
        const result = await this.saveLabExam({
          labExam: this.editingLabExam,
          showMessage: false,
          showError: false,
        });
        if (!result || !result.ok) {
          return this.showRecordError(result && result.error);
        }
        this.showMessage();
        await this.finishSavedRecord();
      } finally {
        this.savingLabExam = false;
      }
    },
    openNewHospitalization() {
      this.beginRecordEditing("hospitalization", {});
    },
    openHospitalizationEdit(hospitalization) {
      this.beginRecordEditing("hospitalization", hospitalization, hospitalization.id);
    },
    closeHospitalizationDialog() {
      this.closeRecordEditor();
    },
    async handleSaveHospitalization() {
      this.savingHospitalization = true;
      try {
        const result = await this.saveHospitalization({
          hospitalization: this.editingHospitalization,
          showMessage: false,
          showError: false,
        });
        if (!result || !result.ok) {
          return this.showRecordError(result && result.error);
        }
        this.showMessage();
        await this.finishSavedRecord();
      } finally {
        this.savingHospitalization = false;
      }
    },
    openPrescription(prescription) {
      // Prescrição enviada abre o resumo em /prescriptions/:id; o rascunho volta
      // para o construtor, que é onde ele ainda pode ser editado.
      this.openPrescriptionFromPatientAction(prescription);
    },
    async loadMoreRecords() {
      if (this.appendingEncounters) return;
      this.appendingEncounters = true;
      try {
        await this.loadMoreTimeline();
      } finally {
        this.appendingEncounters = false;
      }
    },
    /**
     * Mensagem do servidor (`{ message }`): no 400 ela diz exatamente o que
     * falta, e repetir a ação sem corrigir nunca resolve.
     */
    getServerMessage(error) {
      const data = error && error.response ? error.response.data : null;
      const message =
        data && typeof data.message === "string" ? data.message.trim() : "";
      return message || "";
    },
    getRecordErrorMessage(error, fallbackLabel = null) {
      // `ERROR` vive em `constants/messages.js`, não em labels: sem este fallback
      // o alerta apareceria vazio quando a ação falha sem status conhecido.
      const fallback =
        (fallbackLabel && this._getLabel(fallbackLabel)) ||
        this._getMessage("ERROR");
      const status = error && error.response ? error.response.status : null;
      if (status === 403) return this._getLabel("RECORD_FINALIZE_FORBIDDEN");
      if (status === 409) {
        return (
          this.getServerMessage(error) ||
          this._getLabel("RECORD_FINALIZE_CONFLICT")
        );
      }
      if (status === 400) {
        return this.getServerMessage(error) || fallback;
      }
      return fallback;
    },
    showRecordError(error, fallbackLabel = null) {
      this.showAlert({
        text: this.getRecordErrorMessage(error, fallbackLabel),
        color: "error",
      });
    },
    confirmFinalizeVaccination(vaccination) {
      if (!vaccination || vaccination.finalizedAt) return;
      // O backend exige vaccine, manufacturer, batch e applicationDate. A mesma
      // regra aqui evita uma ida ao servidor para devolver a lista de campos em
      // ingles ao usuario.
      const missing = ["vaccine", "manufacturer", "batch"].some(
        (field) => !(vaccination[field] || "").toString().trim()
      );
      if (missing || !vaccination.applicationDate) {
        return this.showAlert({
          text: this._getLabel("VACCINATION_FINALIZE_REQUIRED"),
          color: "error",
        });
      }
      this.setConfirmModel({
        text: this._getLabel("FINALIZE_VACCINATION_CONFIRM"),
        confirmText: this._getLabel("FINALIZE_RECORD_CONFIRM_ACTION"),
        closeText: this._getLabel("CANCEL"),
        callback: () => this.finalizeVaccination(vaccination),
      });
    },
    /**
     * O backend monta o registro a partir do corpo enviado, então a finalização
     * reenvia o conteúdo inteiro da vacina junto de `finalize: true`.
     */
    async finalizeVaccination(vaccination) {
      const result = await this.saveVaccination({
        vaccination: this.buildRecordPayload(vaccination),
        finalize: true,
        showMessage: false,
        showError: false,
      });
      if (!result || !result.ok) {
        return this.showRecordError(
          result && result.error,
          "RECORD_FINALIZE_ERROR"
        );
      }
      this.showAlert({
        text: this._getLabel("RECORD_FINALIZED_SUCCESS"),
        color: "success",
      });
    },
    confirmFinalizeLabExam(labExam) {
      if (!labExam || labExam.finalizedAt) return;
      // O backend so finaliza exame com resultado preenchido ou laudo anexado.
      const hasResults = !!(labExam.results || "").toString().trim();
      const hasFiles = ((labExam.fileUrls || []).length || 0) > 0;
      if (!hasResults && !hasFiles) {
        return this.showAlert({
          text: this._getLabel("LAB_EXAM_FINALIZE_REQUIRED"),
          color: "error",
        });
      }
      this.setConfirmModel({
        text: this._getLabel("FINALIZE_LAB_EXAM_CONFIRM"),
        confirmText: this._getLabel("FINALIZE_RECORD_CONFIRM_ACTION"),
        closeText: this._getLabel("CANCEL"),
        callback: () => this.finalizeLabExam(labExam),
      });
    },
    async finalizeLabExam(labExam) {
      const result = await this.saveLabExam({
        labExam: this.buildRecordPayload(labExam),
        finalize: true,
        showMessage: false,
        showError: false,
      });
      if (!result || !result.ok) {
        return this.showRecordError(
          result && result.error,
          "RECORD_FINALIZE_ERROR"
        );
      }
      this.showAlert({
        text: this._getLabel("RECORD_FINALIZED_SUCCESS"),
        color: "success",
      });
    },
    /**
     * Remove do payload os campos derivados da linha do tempo e os que o
     * servidor controla; o backend também os ignora, mas enviá-los deixaria o
     * corpo ambíguo.
     */
    buildRecordPayload(record) {
      const payload = { ...(record || {}) };
      const serverControlled = [
        "_type",
        "_date",
        "_draft",
        "status",
        "doctorId",
        "organizationId",
        "createdAt",
        "finalizedAt",
        "finalizedBy",
        "addenda",
        "lastUpdate",
        "deleted",
        // Listas mantidas pelo servidor: nunca voltam no corpo de um save, e a
        // URL assinada do laudo não pode ser reenviada.
        "fileUrls",
        "evolutions",
      ];
      serverControlled.forEach((field) => delete payload[field]);
      return payload;
    },
    confirmRemoveVaccination(vaccination) {
      this.confirmRemoveRecord(vaccination, () =>
        this.deleteVaccination(vaccination.id)
      );
    },
    confirmRemoveLabExam(labExam) {
      this.confirmRemoveRecord(labExam, () => this.deleteLabExam(labExam.id));
    },
    confirmRemoveHospitalization(hospitalization) {
      this.confirmRemoveRecord(hospitalization, () =>
        this.deleteHospitalization(hospitalization.id)
      );
    },
    /**
     * Exclusão só de rascunho: registro finalizado recebe 409 do backend e a
     * ação nem é oferecida no card.
     */
    confirmRemoveRecord(record, removeAction) {
      if (!record || !record.id || record.finalizedAt) return;
      this.setConfirmModel({
        text: this._getLabel("DELETE_RECORD_CONFIRM"),
        confirmText: this._getLabel("DELETE_RECORD_CONFIRM_ACTION"),
        closeText: this._getLabel("CANCEL"),
        callback: async () => {
          const result = await removeAction();
          if (!result || !result.ok) {
            return this.showRecordError(
              result && result.error,
              "RECORD_DELETE_ERROR"
            );
          }
          this.showAlert({
            text: this._getLabel("RECORD_DELETED_SUCCESS"),
            color: "success",
          });
        },
      });
    },
    openLabExamFile(file) {
      // A URL assinada vale 15 minutos e vem apenas da resposta do servidor:
      // nunca é guardada no dispositivo.
      if (!file || !file.url) return;
      window.open(file.url, "_blank");
    },
    async handleAttachLabExamFile({ labExam, file } = {}) {
      if (!labExam || !file) return;
      if (ALLOWED_FILE_TYPES.indexOf(file.type) === -1 || file.size > MAX_FILE_SIZE) {
        return this.showAlert({
          text: this._getLabel("FILE_INVALID"),
          color: "error",
        });
      }
      let base64 = null;
      try {
        base64 = await this.readFileAsBase64(file);
      } catch {
        base64 = null;
      }
      if (!base64) {
        return this.showAlert({
          text: this._getLabel("FILE_READ_ERROR"),
          color: "error",
        });
      }
      const result = await this.uploadLabExamFile({
        labExamId: labExam.id,
        file: base64,
        fileName: file.name,
        contentType: file.type,
      });
      if (!result || !result.ok) {
        return this.showRecordError(result && result.error, "FILE_UPLOAD_ERROR");
      }
      this.showAlert({
        text: this._getLabel("FILE_UPLOAD_SUCCESS"),
        color: "success",
      });
    },
    /**
     * O backend espera o conteúdo em base64 puro; o FileReader devolve uma data
     * URL, então o prefixo `data:<mime>;base64,` é removido.
     */
    readFileAsBase64(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const result = reader.result || "";
          const separator = String(result).indexOf(",");
          resolve(separator === -1 ? null : String(result).slice(separator + 1));
        };
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(file);
      });
    },
    openEvolutionDialog(hospitalization) {
      if (!hospitalization || hospitalization.finalizedAt) return;
      this.evolutionHospitalization = hospitalization;
      this.beginRecordEditing(
        "evolution",
        { notes: "", vitalSigns: {}, medications: "" },
        hospitalization.id
      );
    },
    closeEvolutionDialog() {
      this.closeRecordEditor();
    },
    async handleSaveEvolution() {
      if (!this.evolutionHospitalization || !this.hasEvolutionNotes) return;
      this.savingEvolution = true;
      try {
        const result = await this.addEvolution({
          hospitalizationId: this.evolutionHospitalization.id,
          evolution: {
            notes: (this.editingEvolution.notes || "").trim(),
            vitalSigns: this.editingEvolution.vitalSigns || {},
            medications: this.editingEvolution.medications || "",
          },
        });
        if (!result || !result.ok) {
          return this.showRecordError(result && result.error, "EVOLUTION_ERROR");
        }
        this.showAlert({
          text: this._getLabel("EVOLUTION_SAVED"),
          color: "success",
        });
        await this.finishSavedRecord();
      } finally {
        this.savingEvolution = false;
      }
    },
    openDischargeDialog(hospitalization) {
      if (!hospitalization || hospitalization.finalizedAt) return;
      this.dischargingHospitalization = hospitalization;
      this.beginRecordEditing(
        "discharge",
        {
          dischargeDate: Date.now(),
          dischargeCondition: hospitalization.dischargeCondition || "",
          dischargeSummary: hospitalization.dischargeSummary || "",
        },
        hospitalization.id
      );
    },
    closeDischargeDialog() {
      this.closeRecordEditor();
    },
    confirmDischarge() {
      if (!this.isDischargeComplete) {
        return this.showAlert({
          text: this._getLabel("DISCHARGE_REQUIRED"),
          color: "error",
        });
      }
      this.setConfirmModel({
        text: this._getLabel("DISCHARGE_CONFIRM"),
        confirmText: this._getLabel("DISCHARGE_CONFIRM_ACTION"),
        closeText: this._getLabel("CANCEL"),
        callback: () => this.handleDischarge(),
      });
    },
    /**
     * Alta = finalização da internação: o contrato exige data, condição de
     * saída e resumo no mesmo corpo de `finalize: true`.
     */
    async handleDischarge() {
      const hospitalization = this.dischargingHospitalization;
      if (!hospitalization) return;
      this.savingDischarge = true;
      try {
        const result = await this.saveHospitalization({
          hospitalization: {
            ...this.buildRecordPayload(hospitalization),
            dischargeDate: this.dischargeModel.dischargeDate,
            dischargeCondition: (this.dischargeModel.dischargeCondition || "").trim(),
            dischargeSummary: (this.dischargeModel.dischargeSummary || "").trim(),
          },
          finalize: true,
          showMessage: false,
          showError: false,
        });
        if (!result || !result.ok) {
          return this.showRecordError(
            result && result.error,
            "RECORD_FINALIZE_ERROR"
          );
        }
        this.showAlert({
          text: this._getLabel("DISCHARGE_SUCCESS"),
          color: "success",
        });
        await this.finishSavedRecord();
      } finally {
        this.savingDischarge = false;
      }
    },
    /**
     * Carrega as listas do prontuario do paciente atual.
     * Cada chamada recebe um id proprio: se o usuario trocar de paciente durante
     * as requisicoes, a resposta antiga e descartada em vez de sobrescrever a nova.
     */
    async loadPatientData(patientObj) {
      if (!patientObj || !patientObj.id) return;
      this.loadRequestId += 1;
      const requestId = this.loadRequestId;
      const isStale = () =>
        requestId !== this.loadRequestId ||
        !this.patient ||
        this.patient.id !== patientObj.id;
      this.clearRecords();
      await this.fetchEncounters({ append: false });
      if (isStale()) return;
      await this.fetchVaccinations();
      if (isStale()) return;
      await this.fetchLabExams();
      if (isStale()) return;
      await this.fetchHospitalizations();
      if (isStale()) return;
      await this.fetchPrescriptions();
      if (isStale()) return;
      await this.fetchVaccinationSchedule(patientObj.id);
    },
    /**
     * Resolve o paciente vindo da URL (refresh na rota direta).
     * "ready"    -> a tela segue com o paciente que ja estava no store
     * "selected" -> o paciente acabou de ser trocado; o watcher de `patient`
     *               dispara o carregamento, entao o mounted nao repete
     * "redirect" -> 403/404, a tela ja redirecionou
     */
    async resolvePatientFromRoute() {
      const routePatientId = this.routePatientId;
      if (!routePatientId) return "ready";
      if (this.patient && this.patient.id === routePatientId) return "ready";
      const result = await this.selectPatientById(routePatientId);
      if (result && result.ok) return "selected";
      const status = result ? result.status : null;
      this.showAlert({
        text:
          status === 403
            ? this._getLabel("PATIENT_ACCESS_DENIED")
            : this._getLabel("PATIENT_NOT_FOUND"),
        color: "error",
      });
      this.routePathRedirect({ path: "/patients" });
      return "redirect";
    },
    requestRecordNavigation(next) {
      if (this.recordDraftBusy) return next(false);
      if (!this.recordDraftDirty) return next();
      this.setRecordDialogVisible(this.recordDraftKind, false);
      this.pendingRecordNavigation = next;
      this.recordRecoveryDraft = null;
      this.recordDraftError = "";
      this.recordDraftDialog = true;
    },
    persistOpenRecordDraft() {
      if (this.recordDraftDirty) this.persistRecordDraft();
    },
  },
  watch: {
    editingVaccination: {
      deep: true,
      handler() {
        if (this.recordDraftKind === "vaccination") this.scheduleRecordDraftPersistence();
      },
    },
    editingLabExam: {
      deep: true,
      handler() {
        if (this.recordDraftKind === "labExam") this.scheduleRecordDraftPersistence();
      },
    },
    editingHospitalization: {
      deep: true,
      handler() {
        if (this.recordDraftKind === "hospitalization") this.scheduleRecordDraftPersistence();
      },
    },
    editingEvolution: {
      deep: true,
      handler() {
        if (this.recordDraftKind === "evolution") this.scheduleRecordDraftPersistence();
      },
    },
    dischargeModel: {
      deep: true,
      handler() {
        if (this.recordDraftKind === "discharge") this.scheduleRecordDraftPersistence();
      },
    },
    async routePatientId() {
      await this.resolvePatientFromRoute();
    },
    async patient(newPatient) {
      if (!newPatient) {
        return this.clearRecords();
      }
      await this.loadPatientData(newPatient);
      this._setupScreenSizes(this);
    },
    filteredTimeline() {
      this.$nextTick(() => {
        if (this._clinicalResize) this._clinicalResize();
      });
    },
    // O banner de internação ativa e o de vacinas pendentes vivem no header
    // fixo: quando aparecem ou somem, a altura do conteúdo precisa ser refeita.
    activeHospitalization() {
      this.$nextTick(() => {
        if (this._clinicalResize) this._clinicalResize();
      });
    },
    vaccinationSchedule() {
      this.$nextTick(() => {
        if (this._clinicalResize) this._clinicalResize();
      });
    },
  },
  beforeRouteLeave(to, from, next) {
    this.requestRecordNavigation(next);
  },
  beforeDestroy() {
    this.cancelRecordDraftTimer();
    window.removeEventListener("pagehide", this.persistOpenRecordDraft);
    document.removeEventListener("pause", this.persistOpenRecordDraft);
  },
  async mounted() {
    if (!this.canAccessRoute) {
      return this.routeRedirect({ name: "home" });
    }
    const resolved = await this.resolvePatientFromRoute();
    if (resolved === "redirect") return;
    if (resolved === "ready") {
      if (this.patient) {
        await this.loadPatientData(this.patient);
      }
    }
    window.addEventListener("pagehide", this.persistOpenRecordDraft);
    document.addEventListener("pause", this.persistOpenRecordDraft);
    this._setupScreenSizes(this);
  },
};
</script>
<style scoped>
.clinical-record-action { min-height: 44px; }
.clinical-record-primary-actions {
  display: grid !important;
  gap: 8px;
  grid-template-columns: repeat(5, auto);
  justify-content: start;
}
.clinical-record-menu-trigger { min-width: 44px !important; }
.clinical-record-notices { gap: 8px; }
.clinical-record-notice { min-height: 32px; }
.clinical-record-menu { min-width: 260px; }
.clinical-record-menu .v-list-item { min-height: 44px; }
.clinical-record-dialog-content { min-height: 120px; }
.clinical-record-dialog-actions { width: 100%; }
@media (max-width: 599px) {
  .clinical-record-primary-actions {
    grid-template-columns: repeat(3, minmax(0, 1fr));
    width: 100%;
  }
  .clinical-record-main-action,
  .clinical-record-prescription-action,
  .clinical-record-menu-trigger {
    flex: 1 1 0;
    min-width: 0 !important;
    width: 100%;
  }
  .clinical-record-main-action,
  .clinical-record-prescription-action,
  .clinical-record-menu-trigger {
    padding-left: 10px !important;
    padding-right: 10px !important;
  }
  .clinical-record-action ::v-deep .v-btn__content {
    font-size: 12px;
  }
  .clinical-record-dialog-content,
  .clinical-record-dialog-actions {
    padding-left: 20px !important;
    padding-right: 20px !important;
  }
  .clinical-record-dialog-actions {
    padding-bottom: max(20px, env(safe-area-inset-bottom)) !important;
  }
}
</style>
